import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { getApiUrl, API_ENDPOINTS } from '../utils/api'
import { setUnauthorizedHandler } from '../utils/apiJson'
import { useTenant } from './TenantContext'

// Dashboard page access. Keep in sync with the requireRank()/priority checks
// in platform-api (each value here is the same minimum the API enforces).
// 0 Staff In Training . 4 Junior Admin . 7 Head Admin . 8 Manager
const PAGE_ACCESS = {
  '/staff': 0,
  '/staff/reports': 0,
  '/staff/punishment-history': 0,
  '/staff/staff-roster': 0,
  '/staff/staff-loa': 0,
  '/staff/staff-vote': 0,
  '/staff/staff-hours': 0,
  '/staff/documentation': 0,
  '/staff/awards': 0,
  '/staff/awards/admin': 8,
  '/staff/gang-roster': 7,
  '/staff/gang-cooldown': 7,
  '/staff/radio-locks': 7,
  '/staff/imports-log': 7,
  '/staff/bans-manager': 7,
  '/staff/business-hours': 7,
  // Lookup selects an account-only view below Manager; character APIs stay 8+.
  '/staff/player-lookup': 7,
  '/staff/players': 7, // Legacy account links redirect into Lookup.
  '/staff/player-reimbursement': 8,
  '/staff/activity': 8,
  '/staff/apartments': 8,
  '/staff/premium-drug-logs': 8,
  '/staff/tebex-transactions': 8,
  '/staff/content': 8,
  '/staff/resource-swiper': 8,
  '/staff/realtor': 'realtor',
  '/staff/assets': 'media',
}

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const tenant = useTenant()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Environment variables check removed for production security

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('discord_token')
      if (!token) {
        setLoading(false)
        return
      }

      // Verify token with backend/Discord bot
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH_VERIFY), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
        setIsAuthenticated(true)
      } else {
        // Token invalid or backend unavailable (502), clear it
        if (response.status !== 502) {
          // Only clear token if it's not a server error (502)
          localStorage.removeItem('discord_token')
        }
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      // Silently handle network errors (502, connection refused, etc.)
      // Don't clear token on network errors - might be temporary backend issue
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.warn('Auth check failed - backend may be unavailable:', error.message)
      } else {
        console.error('Auth check failed:', error)
        localStorage.removeItem('discord_token')
      }
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }

  // Silent live re-validation (no loading screen). Runs on every dashboard
  // entry/navigation and when the tab regains focus, so a removed staff member
  // loses access within seconds instead of riding a cached session. The backend
  // /api/auth/verify checks the LIVE Discord role (cache-bypassed), so this is a
  // genuine re-auth each time, not a cache read.
  const lastRevalidate = useRef(0)
  const revalidate = useCallback(async ({ force = false } = {}) => {
    // At most once every 30s - this used to hit Discord on every click.
    if (!force && Date.now() - lastRevalidate.current < 30_000) return
    lastRevalidate.current = Date.now()
    const token = localStorage.getItem('discord_token')
    if (!token) {
      setUser(null)
      setIsAuthenticated(false)
      return
    }
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH_VERIFY), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const next = await response.json()
        // Skip the state update when nothing changed (avoids re-rendering
        // every useAuth consumer on each navigation).
        setUser((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
        setIsAuthenticated(true)
      } else if (response.status === 401 || response.status === 403) {
        // Only a definite "not allowed" ends the session. A Discord/API blip
        // (404/5xx) used to log staff out mid-shift.
        localStorage.removeItem('discord_token')
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch {
      // Network error — leave the session as-is (backend may be momentarily down).
    }
  }, [])

  // Any API call that comes back 401 means the session is gone: drop it so the
  // dashboard shows the sign-in screen instead of a page full of errors.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      localStorage.removeItem('discord_token')
      setUser(null)
      setIsAuthenticated(false)
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  // Re-validate when the staff member returns to the tab.
  useEffect(() => {
    const onFocus = () => { if (localStorage.getItem('discord_token')) revalidate() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [revalidate])

  const login = () => {
    // Discord OAuth redirect, driven by the current tenant's config. The
    // redirect URI is built from the browser's origin so the same code works
    // on mwrp.gg, valleyrp.gg, localhost, and any dev hostname.
    const clientId = tenant.discord.clientId
    const redirectUri = encodeURIComponent(window.location.origin + tenant.discord.callbackPath)
    const scope = 'identify'
    try {
      if (window.location.pathname.startsWith('/staff')) sessionStorage.setItem('staffReturn', window.location.pathname + window.location.search)
    } catch { /* ignore */ }
    window.location.href =
      `https://discord.com/api/oauth2/authorize?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`
  }

  const logout = () => {
    localStorage.removeItem('discord_token')
    setUser(null)
    setIsAuthenticated(false)
    window.location.href = '/'
  }

  // Permission helper functions
  const hasPermission = (requiredPriority) => {
    if (!user?.rank) return false
    return user.rank.priority >= requiredPriority
  }

  // Computed permission values (automatically update when user changes)
  const hasManagerAccess = useMemo(() => {
    if (!user?.rank) return false
    return user.rank.priority >= 8 // Manager priority is 8
  }, [user])

  const isCommunityDirector = useMemo(() => {
    return user?.rank?.name === 'Community Director'
  }, [user])

  const hasRealtorRole = useMemo(() => {
    return user?.hasRealtorRole === true
  }, [user])

  const hasMediaAccessRole = useMemo(() => {
    return user?.hasMediaAccessRole === true
  }, [user])

  // Who can open which dashboard page. One table instead of a chain of
  // if/priority blocks, so it's obvious at a glance and matches the API gates.
  //   number = minimum rank priority, 'realtor' / 'media' = Discord role check.
  // Unknown /staff paths fall through to Manager+ (they 404 inside the layout).
  const canAccessPage = (pagePath) => {
    if (!user?.rank) return false
    const priority = user.rank.priority
    const path = pagePath.replace(/\/+$/, '') || '/staff'
    const rule = PAGE_ACCESS[path] ?? PAGE_ACCESS[Object.keys(PAGE_ACCESS).find((p) => p !== '/staff' && path.startsWith(p + '/'))]
    if (rule === 'realtor') return hasRealtorRole
    if (rule === 'media') return hasMediaAccessRole
    if (typeof rule === 'number') return priority >= rule
    return priority >= 8
  }

  // Check if user can edit (only Community Director)
  const canEdit = () => {
    return isCommunityDirector
  }

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth,
    revalidate,
    hasManagerAccess,
    isCommunityDirector,
    hasRealtorRole,
    hasMediaAccessRole,
    canAccessPage,
    canEdit,
    hasPermission
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

